import React from 'react';

interface ReferenceDocumentMeta {
  reference_document_url?: string;
}

// A prompt marks the exact text to link by wrapping it in `{{...}}`, e.g.
// "...download it here: {{Investigator Declaration Page}}." — this makes
// the link position explicit and readable straight from data/questions.rb,
// rather than inferring it (there's no ambiguity if the wrapped phrase
// happens to repeat elsewhere in the prompt). Only the first marker is
// honored; a prompt needing more than one link isn't a case this supports.
const REFERENCE_DOCUMENT_MARKER = /\{\{([^}]+)\}\}/;

/**
 * Turns a *raw* prompt source (the `prompt_template` field — marker
 * intact) into JSX: plain text, except for the `{{...}}`-marked segment,
 * which becomes a link (opening in a new tab) to
 * `meta.reference_document_url`. Falls back to plain stripped text if
 * there's a marker but no matching url, so the marker itself is never left
 * dangling in the output.
 *
 * This is the only frontend code that should ever read `prompt_template` —
 * everywhere else should just render `question.prompt` directly, which the
 * backend already delivers pre-stripped and display-ready (see the
 * `prompt` field docs in types.ts). Don't call this with `question.prompt`.
 */
export const renderPromptText = (
  promptTemplate: string,
  meta?: ReferenceDocumentMeta
): React.ReactNode => {
  const match = REFERENCE_DOCUMENT_MARKER.exec(promptTemplate);
  if (!match) return promptTemplate;

  const url = meta?.reference_document_url;
  if (!url) return promptTemplate.replace(REFERENCE_DOCUMENT_MARKER, '$1');

  const [fullMatch, label] = match;
  const start = match.index;
  const end = start + fullMatch.length;

  return [
    promptTemplate.slice(0, start),
    <a
      key="reference-document-link"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="underline text-blue-600 hover:text-blue-800"
    >
      {label}
    </a>,
    promptTemplate.slice(end),
  ];
};
